import { useTypedTranslation } from '@/hooks/useTypedTranslation';
import { useFeeCalculator } from '@/hooks/useFeeCalculator';
import { useSliderMarkers } from '@/hooks/useSliderMarkers';
import styles from './FeeCalculatorSection.module.css';
import { useState } from 'react';

interface CalculatorProps {
  markerLabels: string[];
  min: number;
  max: number;
  locale: 'it' | 'es';
  children?: React.ReactNode;
}

export function Calculator({ markerLabels, min, max, locale, children }: CalculatorProps) {
  const { t } = useTypedTranslation();
  const [sliderValue, setSliderValue] = useState(0);
  const { thresholds } = useSliderMarkers({ markerLabels, min, max });
  const { amount, stampFee, base, vat } = useFeeCalculator(sliderValue, locale);

  return (
    <div className={styles.calculatorBox}>
      <div className={styles.pinkSliderLabel}>
        {t(`feeCalculatorSection.pinkSliderLabel${locale === 'it' ? 'Italy' : 'Spain'}`)}
      </div>
      <div className={styles.invoiceValue}>
        <span>{t('feeCalculatorSection.invoiceValue.currency')}</span> {amount}
      </div>
      <div className={styles.sliderWrapper}>
        <input
          type="range"
          min={min}
          max={max}
          step={10}
          value={sliderValue}
          onInput={(e) => setSliderValue(Number(e.currentTarget.value))}
          onChange={(e) => setSliderValue(Number(e.target.value))}
          className={styles.slider}
          style={
            {
              '--progress': (sliderValue - min) / (max - min),
            } as React.CSSProperties
          }
        />
        <div className={styles.sliderMarksOverlay}>
          {thresholds.map((threshold: number, idx: number, arr: number[]) => {
            const isActive = sliderValue >= threshold;
            return (
              <div
                key={threshold}
                className={styles.sliderMark}
                style={{
                  left: `${(idx / (arr.length - 1)) * 100}%`,
                }}
              >
                <div
                  className={isActive ? styles.sliderMarkDotActive : styles.sliderMarkDotInactive}
                />
                <div className={styles.sliderMarkLabel}>{markerLabels[idx]}</div>
              </div>
            );
          })}
        </div>
      </div>
      <div className={styles.feeBreakdown}>
        <div className={styles.breakdownRow}>
          <span className={styles.breakdownLabel}>
            {t('feeCalculatorSection.breakdown.agencyFee')}
          </span>
          <span className={styles.breakdownValue}>
            {t('feeCalculatorSection.invoiceValue.currency')}{' '}
            {(amount * 0.07).toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
        <div className={styles.breakdownRow}>
          <span className={styles.breakdownLabel}>
            {t('feeCalculatorSection.breakdown.stampFee')}
          </span>
          <span className={styles.breakdownValueBold}>
            {t('feeCalculatorSection.invoiceValue.currency')}{' '}
            {stampFee.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
        <div className={styles.breakdownRowSave}>
          <span className={styles.saveLabel}>{t('feeCalculatorSection.breakdown.youSave')}</span>
          <span className={styles.saveValue}>
            {t('feeCalculatorSection.invoiceValue.currency')}{' '}
            {/* {vatSaved.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} */}
            {vat.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
        <div className={styles.breakdownSeparator} />
        <div className={styles.breakdownRowSmall}>
          <span className={styles.priceBreakdown}>
            {t('feeCalculatorSection.breakdown.price', {
              base: `${t('feeCalculatorSection.invoiceValue.currency')}${base.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              vat: `${t('feeCalculatorSection.invoiceValue.currency')}${vat.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            })}
          </span>
        </div>
        <div className={styles.breakdownRowSmall}>
          <span className={styles.vatNote}>{t('feeCalculatorSection.breakdown.vatNote')}</span>
        </div>
      </div>
      {children}
    </div>
  );
}
